import React from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useAuth, usePermissions } from '../contexts/IframeAuthProvider'
import { 
  LayoutDashboard, 
  FolderOpen, 
  Users, 
  ClipboardList, 
  MessageCircle, 
  FileText, 
  Settings, 
  Shield,
  LogOut,
  Bell,
  User,
  Menu,
  X
} from 'lucide-react'
import { cn } from '../lib/utils'
import { Badge, CountBadge } from './ui/Badge'
import { useUnreadCount } from '../lib/hooks'
import { useState } from 'react'

interface LayoutProps {
  children: React.ReactNode
}

export const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { user, logout } = useAuth()
  const permissions = usePermissions()
  const location = useLocation()
  const { data: unreadCount } = useUnreadCount()
  const [sidebarOpen, setSidebarOpen] = useState(false)

  const navigation = [
    { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
    { name: 'Projects', href: '/projects', icon: FolderOpen },
    { name: 'Vendors', href: '/vendors', icon: Users },
    { name: 'Evaluations', href: '/evaluations', icon: ClipboardList },
    { 
      name: 'Communications', 
      href: '/communications', 
      icon: MessageCircle,
      badge: unreadCount?.unread_count || 0
    },
    { name: 'Tasks', href: '/tasks', icon: FileText },
    ...(permissions.canViewAuditLogs ? [{ name: 'Audit', href: '/audit', icon: Shield }] : []),
    ...(permissions.canManageUsers ? [{ name: 'Users', href: '/users', icon: Settings }] : []),
  ]

  const isActivePath = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(path + '/')
  }

  const handleLogout = () => {
    logout()
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 md:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <div className={cn(
        'fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-lg transform transition-transform duration-300 ease-in-out md:relative md:translate-x-0',
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      )}>
        <div className="flex items-center justify-between h-16 px-6 border-b">
          <h1 className="text-xl font-bold text-gray-900">Bid Checker</h1>
          <button
            onClick={() => setSidebarOpen(false)}
            className="md:hidden text-gray-400 hover:text-gray-600"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <nav className="mt-8">
          <ul className="space-y-1 px-3">
            {navigation.map((item) => (
              <li key={item.name}>
                <Link
                  to={item.href}
                  className={cn(
                    'flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors',
                    isActivePath(item.href)
                      ? 'bg-blue-50 text-blue-700'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  )}
                  onClick={() => setSidebarOpen(false)}
                >
                  <item.icon className="w-5 h-5 mr-3" />
                  {item.name}
                  {item.badge && item.badge > 0 && (
                    <CountBadge count={item.badge} className="ml-auto" />
                  )}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        {/* User profile section */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t bg-white">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                <User className="w-5 h-5 text-white" />
              </div>
            </div>
            <div className="ml-3 flex-1">
              <p className="text-sm font-medium text-gray-900">{user?.full_name}</p>
              <p className="text-xs text-gray-500">{user?.email}</p>
            </div>
            <button
              onClick={handleLogout}
              className="text-gray-400 hover:text-gray-600 ml-2"
              title="Logout"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="md:ml-64">
        {/* Top bar */}
        <div className="bg-white shadow-sm border-b">
          <div className="flex items-center justify-between h-16 px-6">
            <div className="flex items-center">
              <button
                onClick={() => setSidebarOpen(true)}
                className="md:hidden text-gray-400 hover:text-gray-600 mr-4"
              >
                <Menu className="w-6 h-6" />
              </button>
              <h2 className="text-lg font-semibold text-gray-900">
                {navigation.find(item => isActivePath(item.href))?.name || 'Bid Checker'}
              </h2>
            </div>

            <div className="flex items-center space-x-4">
              {/* Notifications */}
              <Link
                to="/communications"
                className="relative text-gray-400 hover:text-gray-600"
              >
                <Bell className="w-6 h-6" />
                {unreadCount?.unread_count && unreadCount.unread_count > 0 && (
                  <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                    {unreadCount.unread_count > 9 ? '9+' : unreadCount.unread_count}
                  </span>
                )}
              </Link>

              {/* Profile */}
              <Link
                to="/profile"
                className="flex items-center space-x-3 text-gray-700 hover:text-gray-900"
              >
                <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                  <User className="w-5 h-5 text-white" />
                </div>
                <div className="hidden md:block">
                  <p className="text-sm font-medium">{user?.full_name}</p>
                  <div className="flex items-center space-x-1">
                    {user?.roles?.map((role) => (
                      <Badge key={role} variant="secondary" size="sm">
                        {role}
                      </Badge>
                    ))}
                  </div>
                </div>
              </Link>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="p-6">
          {children}
        </main>
      </div>

    </div>
  )
}

export default Layout